import {
  ListAppliedFilterChips,
  ListFiltersDrawer,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OperatorDetailLocationsTable } from "./operator-detail-locations-table.component";
import { OperatorDetailLocationsToolbar } from "./operator-detail-locations-toolbar.component";
import type { OperatorDetail, OperatorDetailLocationRow } from "../types";

const LOCATIONS_PAGE_SIZE = 10;

/** Demo locations for operator detail (aviro-only); replace with API when wired. */
const OPERATOR_DETAIL_LOCATIONS_BY_ID: Record<string, OperatorDetailLocationRow[]> = {
  "operator-row-1": [
    {
      id: "op-loc-1",
      locationName: "Forest View",
      addressLine1: "1200 Industrial Parkway",
      city: "Houston",
      state: "TX",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-2",
      locationName: "Baytown",
      addressLine1: "88 Refinery Row",
      city: "Baytown",
      state: "TX",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-3",
      locationName: "North Yard",
      addressLine1: "4500 Logistics Drive",
      city: "Dallas",
      state: "TX",
      country: "United States",
      status: "inactive",
    },
    {
      id: "op-loc-4",
      locationName: "Lakefront Terminal",
      addressLine1: "200 Harbor Way",
      city: "Chicago",
      state: "IL",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-5",
      locationName: "Midwest Hub",
      addressLine1: "17 Distribution Court",
      city: "Indianapolis",
      state: "IN",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-6",
      locationName: "Pacific Gate",
      addressLine1: "900 Container Lane",
      city: "Long Beach",
      state: "CA",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-7",
      locationName: "Calgary North",
      addressLine1: "3300 Freight Road NE",
      city: "Calgary",
      state: "AB",
      country: "Canada",
      status: "active",
    },
    {
      id: "op-loc-8",
      locationName: "Toronto Exchange",
      addressLine1: "55 Rail Park Avenue",
      city: "Toronto",
      state: "ON",
      country: "Canada",
      status: "inactive",
    },
    {
      id: "op-loc-9",
      locationName: "Montréal Depot",
      addressLine1: "1800 Rue du Port",
      city: "Montréal",
      state: "QC",
      country: "Canada",
      status: "active",
    },
    {
      id: "op-loc-10",
      locationName: "Vancouver Coastal",
      addressLine1: "400 Marine Drive",
      city: "Vancouver",
      state: "BC",
      country: "Canada",
      status: "active",
    },
    {
      id: "op-loc-11",
      locationName: "Seattle Intermodal",
      addressLine1: "2500 Terminal Street S",
      city: "Seattle",
      state: "WA",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-12",
      locationName: "Denver Mountain",
      addressLine1: "6700 Commerce Boulevard",
      city: "Denver",
      state: "CO",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-13",
      locationName: "Phoenix Sunbelt",
      addressLine1: "14200 West Logistics Way",
      city: "Phoenix",
      state: "AZ",
      country: "United States",
      status: "inactive",
    },
    {
      id: "op-loc-14",
      locationName: "Atlanta Southeast",
      addressLine1: "8000 Peachtree Industrial",
      city: "Atlanta",
      state: "GA",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-15",
      locationName: "Miami Gateway",
      addressLine1: "120 Port Boulevard",
      city: "Miami",
      state: "FL",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-16",
      locationName: "Newark Meadowlands",
      addressLine1: "1 Intermodal Plaza",
      city: "Newark",
      state: "NJ",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-17",
      locationName: "Boston Harbor",
      addressLine1: "500 Shipyard Road",
      city: "Boston",
      state: "MA",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-18",
      locationName: "Detroit Assembly",
      addressLine1: "2200 Assembly Park",
      city: "Detroit",
      state: "MI",
      country: "United States",
      status: "inactive",
    },
    {
      id: "op-loc-19",
      locationName: "Kansas City Crossroads",
      addressLine1: "8900 Rail Corridor",
      city: "Kansas City",
      state: "MO",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-20",
      locationName: "Memphis Riverport",
      addressLine1: "3000 Riverside Drive",
      city: "Memphis",
      state: "TN",
      country: "United States",
      status: "active",
    },
    {
      id: "op-loc-21",
      locationName: "Edmonton Yard",
      addressLine1: "7600 Yellowhead Trail",
      city: "Edmonton",
      state: "AB",
      country: "Canada",
      status: "active",
    },
    {
      id: "op-loc-22",
      locationName: "Halifax Atlantic",
      addressLine1: "100 Pier Road",
      city: "Halifax",
      state: "NS",
      country: "Canada",
      status: "active",
    },
  ],
};

const DEFAULT_LOCATIONS = OPERATOR_DETAIL_LOCATIONS_BY_ID["operator-row-1"] ?? [];

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Enabled" },
  { value: "inactive", label: "Disabled" },
] as const;

export interface OperatorDetailLocationsProps {
  operator: OperatorDetail;
}

export function OperatorDetailLocations({ operator }: OperatorDetailLocationsProps) {
  const [searchValue, setSearchValue] = useState("");
  const [sortDescending, setSortDescending] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<Record<string, string[]>>({});
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([]);

  const sourceRows = useMemo(() => {
    return OPERATOR_DETAIL_LOCATIONS_BY_ID[operator.id] ?? DEFAULT_LOCATIONS;
  }, [operator.id]);

  const listFilterSections = useMemo(
    () => [
      { id: "status", label: "Status", options: [...STATUS_FILTER_OPTIONS] },
    ],
    [],
  );

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const items: ListAppliedFilterChipItem[] = [];
    for (const v of appliedStatusFilters) {
      const option = STATUS_FILTER_OPTIONS.find((opt) => opt.value === v);
      items.push({
        id: `status:${v}`,
        label: option?.label ?? v,
        onRemove: () => {
          setAppliedStatusFilters((prev) => prev.filter((val) => val !== v));
          setListPage(1);
        },
      });
    }
    return items;
  }, [appliedStatusFilters]);

  const handleClearAllFilterChips = useCallback(() => {
    setAppliedStatusFilters([]);
    setListPage(1);
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const rows = sourceRows.filter((row) => {
      if (appliedStatusFilters.length > 0 && !appliedStatusFilters.includes(row.status)) {
        return false;
      }
      if (q.length === 0) return true;
      const statusUi = row.status === "active" ? "enabled" : "disabled";
      const haystack = [
        row.locationName,
        row.addressLine1,
        row.city,
        row.state,
        row.status,
        statusUi,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const result = a.locationName.localeCompare(b.locationName, undefined, { sensitivity: "base" });
      return sortDescending ? result * -1 : result;
    });
    return sorted;
  }, [
    appliedStatusFilters,
    searchValue,
    sortDescending,
    sourceRows,
  ]);

  const handleSearchTrigger = useCallback((value: string) => {
    setSearchValue(value);
    setListPage(1);
  }, []);

  const handleLocationSortToggle = useCallback(() => {
    setSortDescending((current) => !current);
  }, []);

  const handleOpenFilterDrawer = useCallback(() => {
    setDraftFilters({
      status: [...appliedStatusFilters],
    });
    setFilterDrawerOpen(true);
  }, [appliedStatusFilters]);

  const handleApplyFilters = useCallback((nextFilters: Record<string, string[]>) => {
    setAppliedStatusFilters(nextFilters.status ?? []);
    setListPage(1);
    setFilterDrawerOpen(false);
  }, []);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / LOCATIONS_PAGE_SIZE));
    if (listPage > pageCount) {
      setListPage(pageCount);
    }
  }, [filteredRows.length, listPage]);

  useEffect(() => {
    setSelectedLocationIds([]);
    setAppliedStatusFilters([]);
    setFilterDrawerOpen(false);
    setDraftFilters({});
  }, [operator.id]);

  const pagedRows = useMemo(() => {
    const fromIndex = (listPage - 1) * LOCATIONS_PAGE_SIZE;
    return filteredRows.slice(fromIndex, fromIndex + LOCATIONS_PAGE_SIZE);
  }, [filteredRows, listPage]);

  return (
    <div className="operator-detail-locations">
      <OperatorDetailLocationsToolbar
        searchValue={searchValue}
        onSearchTrigger={handleSearchTrigger}
        onFilterClick={handleOpenFilterDrawer}
      />
      <ListAppliedFilterChips
        items={appliedChipItems}
        onClearAll={handleClearAllFilterChips}
        totalResults={filteredRows.length}
      />
      <div className="users-page__table-scroll">
        <OperatorDetailLocationsTable
          rows={pagedRows}
          selectedLocationIds={selectedLocationIds}
          onSelectedLocationIdsChange={setSelectedLocationIds}
          sortDescending={sortDescending}
          onLocationNameHeaderClick={handleLocationSortToggle}
          paginationFooter={
            <ListTablePaginationBar
              totalCount={filteredRows.length}
              page={listPage}
              pageSize={LOCATIONS_PAGE_SIZE}
              onPageChange={setListPage}
            />
          }
        />
      </div>
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
        subtitle="Select filters for locations."
        sections={listFilterSections}
        value={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}
